import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { useReactToPrint } from 'react-to-print';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { PageTitle } from '../../../_metronic/layout/core';
import {
    Loader,
    enumColorType,
    enumRoofType,
    enumLedType,
    enumVerandaModelType
} from '../../../_metronic/helpers';
import {
    Card,
    CardBody,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from 'reactstrap';
import classnames from 'classnames';
import {
    clearCalculatorState,
    resetCalculator,
    calculateVeranda,
} from './verandaCalculatorActions';
import VerandaCalculatorPrint from './verandaCalculatorPrint';

import './verandaCalculator.scss';

const placeholderOptions = {
    roofTypes: [
        { value: enumRoofType.Polycarbonate, label: 'Polycarbonaat' },
        { value: enumRoofType.Glass, label: 'Glas' }],
    materialColorTypes: {
        Polycarbonaat: [
            { value: enumColorType.Opaal, label: 'Opaal' },
            { value: enumColorType.Helder, label: 'Helder' }],
        Glas: [
            { value: enumColorType.Helder, label: 'Helder' },
            { value: enumColorType.Opaal, label: 'Opaal' },
            { value: enumColorType.Getint, label: 'Getint' }]
    },
    models: [
        { value: enumVerandaModelType.Deluxe, label: 'Deluxe' },
        { value: enumVerandaModelType.Cube, label: 'Cube' },
        { value: enumVerandaModelType.Classic, label: 'Classic' }],
    frameColors: [
        { value: enumColorType.Anthracite, label: 'Anthracite (RAL 7016)' },
        { value: enumColorType.Zwart, label: 'Zwart (RAL 9005)' },
        { value: enumColorType.Creme, label: 'Creme (RAL 9001)' }],
    ledColors: [
        { value: enumColorType.Wit, label: 'Wit' },
        { value: enumColorType.Warm, label: 'Warm' }],
    ledTypes: [
        { value: enumLedType.Led_Normaal, label: 'Normaal' },
        { value: enumLedType.Led_Dimbaar, label: 'Dimbaar' },
        { value: enumLedType.Alleen_Gat, label: 'Alleen gat' }],
};

// Helper function to find the label for a given value in an options array
const getLabelForValue = (optionsArray, value) => {
    // Special handling for materialColorType which depends on roofType
    if (typeof optionsArray === 'object' && !Array.isArray(optionsArray) && optionsArray !== null) {
        // Assuming it's the materialColorTypes object { Polycarbonaat: [...], Glas: [...] }
        // We need the roofType value to determine the correct sub-array,
        // but this helper is generic. We'll handle this lookup where it's called.
        return value; // Return value itself if lookup is complex here
    }

    if (!Array.isArray(optionsArray)) {
        return value; // Return value if optionsArray is not an array
    }

    const option = optionsArray.find(opt => String(opt.value) === String(value));
    return option ? option.label : value; // Return label if found, otherwise the original value
};


const VerandaCalculator = (props) => {
    const intl = useIntl();
    const printComponentRef = useRef(null);
    const [currentmaterialColorTypes, setCurrentMaterialColorTypes] = useState(placeholderOptions.materialColorTypes['Polycarbonaat']);
    const [editableMaterials, setEditableMaterials] = useState([]);
    const [activeTab, setActiveTab] = useState('1');

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const {
        loading,
        error,
        success,
        calculatedMaterials,
        options
    } = props;

    const {
        clearCalculatorState,
        resetCalculator,
        calculateVeranda,
    } = props;

    const actualOptions = {
        roofTypes: options?.roofTypes?.length ? options.roofTypes : placeholderOptions.roofTypes,
        models: options?.models?.length ? options.models : placeholderOptions.models,
        frameColors: options?.frameColors?.length ? options.frameColors : placeholderOptions.frameColors,
        ledTypes: options?.ledTypes?.length ? options.ledTypes : placeholderOptions.ledTypes,
        ledColors: options?.ledColors?.length ? options.ledColors : placeholderOptions.ledColors,
    };

    const [currentLedColors, setCurrentLedColors] = useState(actualOptions.ledColors);

    useEffect(() => {
        return () => { // Cleanup function
            clearCalculatorState();
        };
    }, [clearCalculatorState]);

    useEffect(() => {
        if (calculatedMaterials) {
            const editableMaterials = calculatedMaterials.map(item => {
                // Create a copy of the item
                const newItem = { ...item };

                // Add the size field with the specified logic
                newItem.size = (() => {
                    if (item.additionalAttributesJson === null || item.additionalAttributesJson === '') {
                        return '';
                    }

                    const removeCm = (value) => {
                        if (typeof value === 'string') {
                            return value.replace(/\s*cm\b/g, '');
                        }
                        return value;
                    };

                    const parts = [];
                    try {
                        const detail = JSON.parse(item.additionalAttributesJson);

                        // Special case for Width and Height combination
                        if (detail.Width && detail.Height) {
                            parts.push(`${detail.Width} x ${detail.Height}`);
                        } else {
                            // Individual handling for Width and Height when only one exists
                            if (detail.Width) parts.push(detail.Width);
                            if (detail.Height && !detail.Width) parts.push(detail.Height);
                        }

                        // Add other dimensions if they exist
                        if (detail.Depth) parts.push(detail.Depth);
                        if (detail.Length) parts.push(detail.Length);
                        if (detail.FreeText) parts.push(removeCm(detail.FreeText));

                        return parts.join(' ');
                    } catch (error) {
                        // Handle potential JSON parsing errors
                        console.error('Error parsing additionalAttributesJson:', error);
                        return '';
                    }
                })();

                return newItem;
            });

            setEditableMaterials(editableMaterials);
        } else {
            setEditableMaterials([]); // Reset if calculatedMaterials is cleared
        }
    }, [calculatedMaterials]);


    useEffect(() => {
        if (success) {
            toast.success(intl.formatMessage({ id: success }));
        }
    }, [success, intl]);

    useEffect(() => {
        if (error) {
            const errorMessage = typeof error === 'string'
                ? error
                : error?.message || intl.formatMessage({ id: 'ERROR.UNKNOWN' });
            toast.error(errorMessage);
        }
    }, [error, intl]);

    const validationSchema = Yup.object().shape({
        width: Yup.number()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' }))
            .positive(intl.formatMessage({ id: 'VALIDATION.POSITIVE_NUMBER' })),
        depth: Yup.number()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' }))
            .positive(intl.formatMessage({ id: 'VALIDATION.POSITIVE_NUMBER' })),
        roofType: Yup.string()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        materialColorType: Yup.string()
            .when('roofType', ([roofType], schema) => {
                const materialColorTypes = (options?.materialColorTypes?.[roofType] || placeholderOptions.materialColorTypes[roofType] || []);
                return materialColorTypes.length > 0
                    ? schema.required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' }))
                    : schema;
            }),
        model: Yup.string()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        frameColor: Yup.string()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        nrOfLedPerLigger: Yup.number()
            .min(0, intl.formatMessage({ id: 'VALIDATION.MIN_VALUE_ZERO' }))
            .integer()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        ledType: Yup.string().required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        ledColor: Yup.string().required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
        referenceNumber: Yup.string().nullable(),
        notes: Yup.string().nullable(),
        customerInfo: Yup.string().nullable(),
        maxDepth: Yup.number().positive(intl.formatMessage({ id: 'VALIDATION.POSITIVE_NUMBER' })).required(intl.formatMessage({ id: 'VALIDATION.REQUIRED_FIELD' })),
    });

    const initialValues = {
        width: '',
        depth: '',
        roofType: enumRoofType.Polycarbonate,
        materialColorType: enumColorType.Opaal,
        maxDepth: 98.4,
        model: enumVerandaModelType.Deluxe,
        frameColor: enumColorType.Anthracite,
        nrOfLedPerLigger: 2,
        ledType: enumLedType.Led_Normaal,
        ledColor: enumColorType.Wit,
        referenceNumber: '',
        notes: '',
        customerInfo: '',
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            console.log("Form Submitted:", values);
            // Exclude LED parameters when sending variables to backend
            const { nrOfLedPerLigger, ledType, ledColor, ...valuesWithoutLed } = values;
            calculateVeranda({
                ...valuesWithoutLed,
                isBqb: activeTab === '2'
            });
        },
    });

    useEffect(() => {
        const ledType = Number(formik.values.ledType);
        if (ledType === enumLedType.Led_Dimbaar) {
            setCurrentLedColors([
                { value: enumColorType.Wit, label: 'Wit' },
                { value: enumColorType.Warm, label: 'Warmwit' },
                { value: 'TweeKleuren', label: 'Twee Kleuren' }
            ]);
        } else if (ledType === enumLedType.Led_Normaal) {
            setCurrentLedColors([
                { value: enumColorType.Wit, label: 'Wit' },
                { value: enumColorType.Warm, label: 'Warmwit' }
            ]);
        } else {
            setCurrentLedColors(actualOptions.ledColors);
        }
    }, [formik.values.ledType, actualOptions.ledColors]);

    const handleReset = () => {
        formik.resetForm();
        resetCalculator();
        setEditableMaterials([]);
    };

    const handleMaterialChange = (index, field, value) => {
        const updatedMaterials = [...editableMaterials];
        updatedMaterials[index] = {
            ...updatedMaterials[index],
            [field]: field === 'count' ? parseInt(value, 10) || 0 : value // Ensure count is a number
        };
        setEditableMaterials(updatedMaterials);
    };

    const handleRoofTypeChange = (event) => {
        const selectedType = Number(event.target.value) == enumRoofType.Polycarbonate ? 'Polycarbonaat' : 'Glas';
        formik.handleChange(event);
        formik.setFieldValue('materialColorType', ''); // Reset sub-type

        // Update available materialColorTypes
        const materialColorTypes = options?.materialColorTypes?.[selectedType] || placeholderOptions.materialColorTypes[selectedType] || [];
        setCurrentMaterialColorTypes(materialColorTypes);

        // Update 'Max Depth' based on selectedType
        const maxDepthValue = selectedType === 'Polycarbonaat' ? 98.4 : (selectedType === 'Glas' ? 85.0 : '');
        formik.setFieldValue('maxDepth', maxDepthValue);
    };

    const renderFormField = (labelId, name, type = 'text', options = [], readOnly = false, step = undefined, disabled = false) => {
        const isTouched = formik.touched[name];
        const errorMessage = formik.errors[name];

        const handleChange = (e) => {
            if (name === 'roofType') {
                handleRoofTypeChange(e);
            } else {
                formik.handleChange(e);
            }
        }

        return (
            <FormGroup className="mb-2">
                <Label for={name} className="fw-bolder fs-7 mb-1">
                    {intl.formatMessage({ id: labelId })}
                </Label>
                <Input
                    id={name}
                    name={name}
                    type={type}
                    bsSize="sm"
                    value={formik.values[name] ?? ''}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    invalid={isTouched && !!errorMessage}
                    readOnly={readOnly}
                    disabled={readOnly || disabled}
                    step={step}
                    className={type === 'textarea' ? 'form-textarea' : ''}
                    style={type === 'textarea' ? { minHeight: '80px', height: '80px' } : {}}
                >
                    {type === 'select' && <option value="">{intl.formatMessage({ id: 'GENERAL.SELECT_OPTION' })}</option>}
                    {type === 'select' && options.map(opt => (
                        <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
                    ))}
                </Input>
                {isTouched && errorMessage && <div className='fv-plugins-message-container invalid-feedback'>{errorMessage}</div>}
            </FormGroup>
        );
    };

    const handleReactPrint = useReactToPrint({
        content: () => printComponentRef.current,
        documentTitle: (() => {
            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
            const year = today.getFullYear();
            return `Veranda_${day}${month}${year}`;
        })()
    });


    const breadcrumbs = [
        {
            title: intl.formatMessage({ id: 'MENU.TOOLS' }),
            path: '/veranda-calculator',
            isSeparator: false,
            isActive: false,
        },
        { title: '', path: '', isSeparator: true, isActive: false },
    ];

    const companyName = "The Garden Gallery";
    const cdnDomain = process.env.REACT_APP_CDN_DOMAIN;
    const logoUrl = `${cdnDomain}/company/logo.png`;

    return (
        <div className="veranda-calculator-page">
            <div>
                <PageTitle breadcrumbs={breadcrumbs}>
                    {intl.formatMessage({ id: 'VERANDA_CALCULATOR.PAGE_TITLE' })}
                </PageTitle>
            </div>

            {loading && <Loader />}

            <Card className="mb-4 shadow-sm">
                <CardBody className="p-4">
                    <Nav tabs className="mb-4">
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '1' })}
                                onClick={() => { toggleTab('1'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                Standard Veranda
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '2' })}
                                onClick={() => { toggleTab('2'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                BQB Veranda
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                            <Form onSubmit={formik.handleSubmit} noValidate>
                                {/* Section 1: Dimensions & Roof */}
                                <div className="mb-4">
                            <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                <i className="fas fa-ruler-combined me-2 fs-8"></i>
                                {intl.formatMessage({ id: 'VERANDA_CALCULATOR.DIMENSIONS_SECTION' })} & {intl.formatMessage({ id: 'VERANDA_CALCULATOR.MATERIAL' })}
                            </h6>
                            <Row className="g-3">
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.WIDTH', 'width', 'number')}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.DEPTH', 'depth', 'number')}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('VERANDA_CALCULATOR.MAX_DEPTH', 'maxDepth', 'number', [], false, 0.1)}
                                </Col>
                                <Col md={3}>
                                    {renderFormField('CHECKOUT.INPUT.ROOFTYPE', 'roofType', 'select', actualOptions.roofTypes)}
                                </Col>
                                <Col md={3}>
                                    {renderFormField('CHECKOUT.INPUT.MATERIALCOLOR', 'materialColorType', 'select', currentmaterialColorTypes)}
                                </Col>
                            </Row>
                        </div>

                        {/* Section 2: Style & LED */}
                        <div className="mb-4">
                            <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                <i className="fas fa-palette me-2 fs-8"></i>
                                {intl.formatMessage({ id: 'VERANDA_CALCULATOR.STYLE_SECTION' })} & {intl.formatMessage({ id: 'VERANDA_CALCULATOR.LED' })}
                            </h6>
                            <Row className="g-3">
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.MODELTYPE', 'model', 'select', actualOptions.models)}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.FRAMECOLOR', 'frameColor', 'select', actualOptions.frameColors)}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.NROFLEDPERCOLUMN', 'nrOfLedPerLigger', 'number')}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.LEDTYPE', 'ledType', 'select', actualOptions.ledTypes, false, undefined, Number(formik.values.nrOfLedPerLigger) === 0)}
                                </Col>
                                <Col md={2}>
                                    {renderFormField('CHECKOUT.INPUT.LEDCOLOR', 'ledColor', 'select', currentLedColors, false, undefined, Number(formik.values.nrOfLedPerLigger) === 0 || Number(formik.values.ledType) === enumLedType.Alleen_Gat)}
                                </Col>
                            </Row>
                        </div>

                        {/* Section 3: Customer Info */}
                        <div className="mb-2">
                            <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                <i className="fas fa-user me-2 fs-8"></i>
                                {intl.formatMessage({ id: 'VERANDA_CALCULATOR.CUSTOMER_INFO_SECTION' })}
                            </h6>
                            <Row className="g-3">
                                <Col md={3}>
                                    {renderFormField('VERANDA_CALCULATOR.REFERENCE_NUMBER', 'referenceNumber', 'text')}
                                </Col>
                                <Col md={4}>
                                    {renderFormField('VERANDA_CALCULATOR.CUSTOMER_INFO', 'customerInfo', 'textarea')}
                                </Col>
                                <Col md={5}>
                                    {renderFormField('VERANDA_CALCULATOR.NOTES', 'notes', 'textarea')}
                                </Col>
                            </Row>
                        </div>

                                {/* Action Buttons */}
                                <Row className="mt-3 no-print">
                                    <Col className="text-end">
                                        <Button color="light" type="button" className="me-3 btn-sm" onClick={handleReset}>
                                            <i className="fas fa-undo me-2"></i>
                                            {intl.formatMessage({ id: 'GENERAL.RESET' })}
                                        </Button>
                                        <Button color="primary" type="submit" className="btn-sm" disabled={loading || !formik.isValid || !formik.dirty}>
                                            <i className="fas fa-calculator me-2"></i>
                                            {intl.formatMessage({ id: 'VERANDA_CALCULATOR.CALCULATE' })}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </TabPane>
                        <TabPane tabId="2">
                            <Form onSubmit={formik.handleSubmit} noValidate>
                                {/* Reusing the same form for BQB, as inputs are compatible */}
                                {/* Section 1: Dimensions & Roof */}
                                <div className="mb-4">
                                    <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                        <i className="fas fa-ruler-combined me-2 fs-8"></i>
                                        {intl.formatMessage({ id: 'VERANDA_CALCULATOR.DIMENSIONS_SECTION' })} & {intl.formatMessage({ id: 'VERANDA_CALCULATOR.MATERIAL' })}
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.WIDTH', 'width', 'number')}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.DEPTH', 'depth', 'number')}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('VERANDA_CALCULATOR.MAX_DEPTH', 'maxDepth', 'number', [], false, 0.1)}
                                        </Col>
                                        <Col md={3}>
                                            {renderFormField('CHECKOUT.INPUT.ROOFTYPE', 'roofType', 'select', actualOptions.roofTypes)}
                                        </Col>
                                        <Col md={3}>
                                            {renderFormField('CHECKOUT.INPUT.MATERIALCOLOR', 'materialColorType', 'select', currentmaterialColorTypes)}
                                        </Col>
                                    </Row>
                                </div>

                                {/* Section 2: Style & LED */}
                                <div className="mb-4">
                                    <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                        <i className="fas fa-palette me-2 fs-8"></i>
                                        {intl.formatMessage({ id: 'VERANDA_CALCULATOR.STYLE_SECTION' })} & {intl.formatMessage({ id: 'VERANDA_CALCULATOR.LED' })}
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.MODELTYPE', 'model', 'select', actualOptions.models)}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.FRAMECOLOR', 'frameColor', 'select', actualOptions.frameColors)}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.NROFLEDPERCOLUMN', 'nrOfLedPerLigger', 'number')}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.LEDTYPE', 'ledType', 'select', actualOptions.ledTypes, false, undefined, Number(formik.values.nrOfLedPerLigger) === 0)}
                                        </Col>
                                        <Col md={2}>
                                            {renderFormField('CHECKOUT.INPUT.LEDCOLOR', 'ledColor', 'select', currentLedColors, false, undefined, Number(formik.values.nrOfLedPerLigger) === 0 || Number(formik.values.ledType) === enumLedType.Alleen_Gat)}
                                        </Col>
                                    </Row>
                                </div>

                                {/* Section 3: Customer Info */}
                                <div className="mb-2">
                                    <h6 className="text-gray-600 fw-bold mb-3 fs-8 text-uppercase">
                                        <i className="fas fa-user me-2 fs-8"></i>
                                        {intl.formatMessage({ id: 'VERANDA_CALCULATOR.CUSTOMER_INFO_SECTION' })}
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={3}>
                                            {renderFormField('VERANDA_CALCULATOR.REFERENCE_NUMBER', 'referenceNumber', 'text')}
                                        </Col>
                                        <Col md={4}>
                                            {renderFormField('VERANDA_CALCULATOR.CUSTOMER_INFO', 'customerInfo', 'textarea')}
                                        </Col>
                                        <Col md={5}>
                                            {renderFormField('VERANDA_CALCULATOR.NOTES', 'notes', 'textarea')}
                                        </Col>
                                    </Row>
                                </div>

                                {/* Action Buttons */}
                                <Row className="mt-3 no-print">
                                    <Col className="text-end">
                                        <Button color="light" type="button" className="me-3 btn-sm" onClick={handleReset}>
                                            <i className="fas fa-undo me-2"></i>
                                            {intl.formatMessage({ id: 'GENERAL.RESET' })}
                                        </Button>
                                        <Button color="primary" type="submit" className="btn-sm" disabled={loading || !formik.isValid || !formik.dirty}>
                                            <i className="fas fa-calculator me-2"></i>
                                            {intl.formatMessage({ id: 'VERANDA_CALCULATOR.CALCULATE' })}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </TabPane>
                    </TabContent>
                </CardBody>
            </Card>

            {/* Results Section */}
            {(editableMaterials && editableMaterials.length > 0) && (
                <Card className="mb-4 shadow-sm results-card">
                    <CardBody className='p-4'>
                        {/* Responsive wrapper for standard HTML table */}
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-striped table-sm align-middle">
                                <thead>
                                    <tr className="fw-bolder text-muted bg-light">
                                        <th className="ps-4 rounded-start">{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.MATERIAL' })}</th>
                                        <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.SIZE' })}</th>
                                        <th className="text-center rounded-end">{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.COUNT' })}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {editableMaterials.map((item, index) => (
                                        <tr key={index}>
                                            <td className="ps-4">
                                                <Input
                                                    type="text"
                                                    bsSize="sm"
                                                    className="form-control-sm"
                                                    value={item.productName}
                                                    onChange={(e) => handleMaterialChange(index, 'productName', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Input
                                                    type="text"
                                                    bsSize="sm"
                                                    className="form-control-sm"
                                                    value={item.size}
                                                    onChange={(e) => handleMaterialChange(index, 'size', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-center" style={{ width: '100px' }}>
                                                <Input
                                                    type="number"
                                                    bsSize="sm"
                                                    className="form-control-sm text-center"
                                                    value={item.quantity}
                                                    onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                                                    min="0"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Row className="mt-3">
                            <Col className="text-end">
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={handleReactPrint}
                                >
                                    <i className="fas fa-print me-2"></i>
                                    {intl.formatMessage({ id: 'GENERAL.PRINT' })}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            )}

            {/* Print Section (Hidden) */}
            <div style={{ display: 'none' }}>
                {(() => {
                    // Prepare formData with labels for printing
                    const formDataWithLabels = {
                        ...formik.values, // Keep original values like width, depth, notes etc.
                        // Add fields with their corresponding labels
                        roofTypeLabel: getLabelForValue(actualOptions.roofTypes, formik.values.roofType),
                        materialColorTypeLabel: getLabelForValue(
                            // Get the correct sub-array based on the selected roofType
                            currentmaterialColorTypes, // Use the state variable holding the current relevant options
                            formik.values.materialColorType
                        ),
                        modelLabel: getLabelForValue(actualOptions.models, formik.values.model),
                        frameColorLabel: getLabelForValue(actualOptions.frameColors, formik.values.frameColor),
                        ledTypeLabel: getLabelForValue(actualOptions.ledTypes, formik.values.ledType),
                        ledColorLabel: getLabelForValue(currentLedColors, formik.values.ledColor),
                    };

                    return (
                        <VerandaCalculatorPrint
                            ref={printComponentRef}
                            formData={formDataWithLabels} // Pass the data with labels
                            calculatedMaterials={editableMaterials}
                            companyName={companyName}
                            logoUrl={logoUrl}
                        />
                    );
                })()}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    const calculatorState = state.VerandaCalculator;
    return {
        loading: calculatorState.loading,
        error: calculatorState.error,
        success: calculatorState.success,
        calculatedMaterials: calculatorState.calculatedMaterials,
        options: {
            roofTypes: calculatorState.options?.roofTypes || [],
            materialColorTypes: calculatorState.options?.materialColorTypes || {},
            models: calculatorState.options?.models || [],
            frameColors: calculatorState.options?.frameColors || [],
            ledTypes: calculatorState.options?.ledTypes || [],
            ledColors: calculatorState.options?.ledColors || [],
        },
    };
};

const mapActionsToProps = {
    clearCalculatorState,
    resetCalculator,
    calculateVeranda,
};

export default connect(mapStateToProps, mapActionsToProps)(VerandaCalculator);
